import { type ResumeState } from '@/types';
import { formatDateRange } from '@/lib/utils';

export function ResumePreview({ state }: { state: ResumeState }) {
  const { profile, experience, education, skills } = state;

  return (
    <div
      className="resume-serif bg-white p-8 text-[13px] leading-relaxed text-[#0A2540]"
      style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
    >
      <header className="border-b-2 border-[#0A2540] pb-4">
        <h1 className="text-2xl font-bold uppercase tracking-wide">{profile.fullName || 'Your Name'}</h1>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#4A5568]">
          {profile.email && <span>{profile.email}</span>}
          {profile.phone && <span>{profile.phone}</span>}
          {profile.location && <span>{profile.location}</span>}
        </div>
      </header>

      {profile.summary && (
        <section className="mt-5">
          <p className="text-sm leading-relaxed">{profile.summary}</p>
        </section>
      )}

      {experience.length > 0 && experience.some((e) => e.company || e.title) && (
        <section className="mt-5">
          <h2 className="mb-2 border-b border-[#0A2540] pb-1 text-sm font-bold uppercase tracking-wider">
            Experience
          </h2>
          <div className="space-y-4">
            {experience
              .filter((e) => e.company || e.title)
              .map((exp) => (
                <div key={exp.id}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <div className="font-bold">{exp.title || 'Job Title'}</div>
                    <div className="text-sm text-[#4A5568]">{formatDateRange(exp.startDate, exp.endDate)}</div>
                  </div>
                  <div className="text-sm font-medium italic text-[#4A5568]">{exp.company || 'Company'}</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {exp.bullets
                      .filter((b) => b.trim())
                      .map((b, idx) => (
                        <li key={idx}>{b}</li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
        </section>
      )}

      {education.length > 0 && education.some((e) => e.school || e.degree) && (
        <section className="mt-5">
          <h2 className="mb-2 border-b border-[#0A2540] pb-1 text-sm font-bold uppercase tracking-wider">
            Education
          </h2>
          <div className="space-y-3">
            {education
              .filter((edu) => edu.school || edu.degree)
              .map((edu) => (
                <div key={edu.id}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <div className="font-bold">{edu.school || 'School'}</div>
                    <div className="text-sm text-[#4A5568]">{edu.graduationDate}</div>
                  </div>
                  <div className="text-sm text-[#4A5568]">
                    {edu.degree} {edu.field && `in ${edu.field}`}
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {skills.length > 0 && (
        <section className="mt-5">
          <h2 className="mb-2 border-b border-[#0A2540] pb-1 text-sm font-bold uppercase tracking-wider">
            Skills
          </h2>
          <p className="text-sm">{skills.join(', ')}</p>
        </section>
      )}
    </div>
  );
}

export default ResumePreview;
